/**
 * preferences-section.tsx — "Tuỳ chọn giao diện" (Cài đặt cá nhân).
 *
 * Ngôn ngữ giao diện (lưu theo tài khoản — `/ui-settings/me` — và trên trình duyệt), chủ đề sáng/tối/theo hệ thống
 * và ghim menu điều hướng (hai mục sau chỉ lưu trên trình duyệt đang dùng). Múi giờ chưa hỗ trợ nên không hiện.
 */
import { Check, Monitor, Moon, Sun, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LanguageSelect } from '@/components/shared/language-select'
import { Switch } from '@/components/ui/misc'
import { useUiStore, type ThemePref } from '@/stores/ui-store'
import { cn } from '@/lib/utils'
import { AccountRow, AccountSection } from './account-ui'

const THEMES: { id: ThemePref; icon: LucideIcon }[] = [
  { id: 'light', icon: Sun },
  { id: 'dark', icon: Moon },
  { id: 'system', icon: Monitor },
]

/** Hình thu nhỏ minh hoạ bố cục ở chế độ sáng/tối. */
function ThemePreview({ id }: { id: ThemePref }) {
  const pane = (dark: boolean) => (
    <div className={cn('flex h-full flex-1 gap-1 p-1.5', dark ? 'bg-slate-900' : 'bg-slate-100')}>
      <div className={cn('w-2.5 rounded-sm', dark ? 'bg-slate-700' : 'bg-primary/25')} />
      <div className={cn('flex flex-1 flex-col gap-1 rounded-sm p-1', dark ? 'bg-slate-800' : 'bg-white')}>
        <div className={cn('h-1.5 w-2/3 rounded-full', dark ? 'bg-slate-600' : 'bg-slate-200')} />
        <div className={cn('h-1.5 w-1/2 rounded-full', dark ? 'bg-slate-600' : 'bg-slate-200')} />
        <div className="mt-auto h-1.5 w-1/3 self-end rounded-full bg-primary/70" />
      </div>
    </div>
  )
  return (
    <div className="flex h-16 overflow-hidden rounded-md border">
      {id === 'system' ? (
        <>
          {pane(false)}
          {pane(true)}
        </>
      ) : (
        pane(id === 'dark')
      )}
    </div>
  )
}

export function PreferencesSection() {
  const { themePref, setThemePref, sidebarCollapsed, setSidebarPinned } = useUiStore()
  const { t } = useTranslation('account')

  return (
    <div className="max-w-3xl space-y-6">
      <AccountSection title={t('preferences.languageTitle')} description={t('preferences.languageDesc')}>
        <AccountRow label={t('preferences.language')}>
          <LanguageSelect id="ui-language" />
        </AccountRow>
      </AccountSection>
      <AccountSection title={t('preferences.title')} description={t('preferences.desc')}>
        <AccountRow label={t('preferences.theme')}>
          <div role="radiogroup" aria-label={t('preferences.theme')} className="flex flex-wrap gap-3">
            {THEMES.map((th) => {
              const Icon = th.icon
              const active = themePref === th.id
              return (
                <button
                  key={th.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setThemePref(th.id)}
                  className={cn(
                    'relative w-32 rounded-lg border p-2 text-left transition-colors',
                    active ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/50',
                  )}
                >
                  <ThemePreview id={th.id} />
                  <span className="mt-2 flex items-center gap-1.5 whitespace-nowrap text-xs font-medium">
                    <Icon className="h-3.5 w-3.5" /> {t(`shell:userMenu.themes.${th.id}`)}
                  </span>
                  {active && (
                    <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="h-3 w-3" />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </AccountRow>
        <AccountRow
          label={t('preferences.pinMenu')}
          hint={t('preferences.pinMenuHint')}
        >
          <Switch
            aria-label={t('preferences.pinMenu')}
            checked={!sidebarCollapsed}
            onCheckedChange={(v) => setSidebarPinned(v)}
          />
        </AccountRow>
      </AccountSection>
    </div>
  )
}
